import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { catalogColors } from './catalogColors';
import { cn } from '@/lib/utils';

interface StageRowProps {
  label: string;
  subLabel?: string;
  count: number;
  priorCount: number;  // denominator for the bar width AND the % display
  color?: string;
  children?: React.ReactNode;  // optional segmented bar override
  isBaseline?: boolean;        // suppresses % and "of N prior" when this row IS the baseline
  className?: string;
}

function fmt(n: number) { return n.toLocaleString(); }

function pctNum(count: number, prior: number) {
  if (!prior) return null;
  return Math.round((count / prior) * 1000) / 10;
}

export function StageRow({ label, subLabel, count, priorCount, color = catalogColors.accentTeal, children, isBaseline = false, className }: StageRowProps) {
  const pct = isBaseline ? null : pctNum(count, priorCount);
  const barWidth = priorCount > 0 ? Math.min(100, (count / priorCount) * 100) : 0;
  const isGood = pct !== null && pct >= 80;

  return (
    <TooltipProvider>
      <div className={cn('flex items-center gap-3 min-h-[32px]', className)}>
        {/* Label column */}
        <div className="w-48 shrink-0 text-right">
          <span className="text-sm font-medium text-foreground">{label}</span>
          {subLabel && <div className="text-[10px] text-muted-foreground">{subLabel}</div>}
        </div>

        {/* Bar column */}
        <div className="flex-1 h-6 rounded overflow-hidden border" style={{ backgroundColor: 'white', borderColor: catalogColors.muted }}>
          {children ?? (
            <div
              className="h-full rounded transition-all duration-500"
              style={{ width: `${barWidth}%`, backgroundColor: color }}
            />
          )}
        </div>

        {/* Count column — outside bar geometry (D4) */}
        <div className="w-32 shrink-0 text-right space-y-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="text-base font-bold tabular-nums cursor-default">{fmt(count)}</div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{fmt(count)} of {fmt(priorCount)}</p>
            </TooltipContent>
          </Tooltip>
          {pct !== null && (
            <div
              className="text-xs font-medium tabular-nums"
              style={{ color: isGood ? catalogColors.accentTeal : catalogColors.warn }}
            >
              {pct}%
            </div>
          )}
          {!isBaseline && priorCount > 0 && (
            <div className="text-[10px] text-muted-foreground">of {fmt(priorCount)}</div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
